/**
 * StatisticsView Component
 *
 * Displays user climbing statistics including quick stats (highest grade, sends, routes),
 * activity over time with period filters, and a grade breakdown horizontal bar chart.
 *
 * @example
 * ```tsx
 * <StatisticsView />
 * ```
 */

import { useState, useMemo, type CSSProperties } from 'react';
import { useUserStatistics, type ActivityDataPoint } from '@/hooks/useUserStatistics';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

/**
 * Time period options for activity filtering
 */
type TimePeriod = 'week' | 'month' | 'year' | 'all';

/**
 * Aggregate activity data by month
 */
const aggregateByMonth = (data: ActivityDataPoint[]): ActivityDataPoint[] => {
  const monthMap = new Map<string, { sends: number; attempts: number }>();

  data.forEach((d) => {
    const monthKey = d.date.substring(0, 7); // YYYY-MM
    const current = monthMap.get(monthKey) ?? { sends: 0, attempts: 0 };
    current.sends += d.sends;
    current.attempts += d.attempts;
    monthMap.set(monthKey, current);
  });

  return Array.from(monthMap.entries())
    .map(([month, totals]) => ({
      date: month,
      sends: totals.sends,
      attempts: totals.attempts,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * StatisticsView displays user climbing statistics
 */
export function StatisticsView() {
  const { statistics, loading } = useUserStatistics();
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('week');

  // All hooks must be called before any early returns to maintain hook ordering
  // Filter activity data based on selected period
  const filteredActivity = useMemo(() => {
    if (!statistics) return [];
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    const activity = statistics.activityByDate;

    let startDate: Date;
    switch (selectedPeriod) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(startDate.getFullYear() - 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'all':
      default:
        // Return all data, possibly aggregated by month if too many days
        if (activity.length > 60) {
          return aggregateByMonth(activity);
        }
        return activity;
    }

    const startStr = startDate.toISOString().split('T')[0] ?? '';
    const filtered = activity.filter((d) => d.date >= startStr);

    // For year view, aggregate by month
    if (selectedPeriod === 'year') {
      return aggregateByMonth(filtered);
    }

    return filtered;
  }, [statistics, selectedPeriod]);

  // Calculate totals for the filtered period
  const periodTotals = useMemo(() => {
    return filteredActivity.reduce(
      (acc, day) => ({
        sends: acc.sends + day.sends,
        attempts: acc.attempts + day.attempts,
      }),
      { sends: 0, attempts: 0 }
    );
  }, [filteredActivity]);

  // Find max value for chart scaling
  const maxActivityValue = useMemo(() => {
    return Math.max(...filteredActivity.map((d) => d.sends + d.attempts), 1);
  }, [filteredActivity]);

  // Filter grades with sends for the breakdown chart
  const gradesWithSends = useMemo(() => {
    if (!statistics) return [];
    return statistics.sendsByGrade
      .filter((g) => g.sendCount > 0)
      .sort((a, b) => {
        // Sort by grade descending (higher grades first)
        const gradeA = parseInt(a.grade.replace('V', ''), 10);
        const gradeB = parseInt(b.grade.replace('V', ''), 10);
        return gradeB - gradeA;
      });
  }, [statistics]);

  // Find max send count for bar scaling
  const maxSendCount = useMemo(() => {
    return Math.max(...gradesWithSends.map((g) => g.sendCount), 1);
  }, [gradesWithSends]);

  // Early returns after all hooks
  if (loading) {
    return <LoadingSpinner />;
  }

  // Empty state when no climbs logged
  if (!statistics || statistics.totalSends === 0) {
    return (
      <div style={containerStyle}>
        <p style={emptyTextStyle}>No climbs logged yet. Start climbing!</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Quick Stats Row */}
      <div style={quickStatsRowStyle}>
        <StatCard value={statistics.highestGrade ?? '-'} label="Highest" />
        <StatCard value={statistics.totalSends.toString()} label="Sends" />
        <StatCard value={statistics.totalAttempts.toString()} label="Attempts" />
        <StatCard value={statistics.uniqueRoutes.toString()} label="Routes" />
      </div>

      {/* Activity Section */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Activity</h3>
        <div style={periodButtonsStyle}>
          <PeriodButton
            label="Week"
            isSelected={selectedPeriod === 'week'}
            onClick={() => setSelectedPeriod('week')}
          />
          <PeriodButton
            label="Month"
            isSelected={selectedPeriod === 'month'}
            onClick={() => setSelectedPeriod('month')}
          />
          <PeriodButton
            label="Year"
            isSelected={selectedPeriod === 'year'}
            onClick={() => setSelectedPeriod('year')}
          />
          <PeriodButton
            label="All Time"
            isSelected={selectedPeriod === 'all'}
            onClick={() => setSelectedPeriod('all')}
          />
        </div>
        {filteredActivity.length > 0 ? (
          <div style={chartContainerStyle}>
            <div style={chartStyle}>
              {filteredActivity.map((day) => (
                <ActivityBar
                  key={day.date}
                  date={day.date}
                  sends={day.sends}
                  attempts={day.attempts}
                  maxValue={maxActivityValue}
                  isMonthly={
                    selectedPeriod === 'year' ||
                    (selectedPeriod === 'all' && filteredActivity.length <= 24)
                  }
                />
              ))}
            </div>
            <div style={legendStyle}>
              <span style={legendItemStyle}>
                <span style={legendDotSendsStyle} />
                Sends ({periodTotals.sends})
              </span>
              <span style={legendItemStyle}>
                <span style={legendDotAttemptsStyle} />
                Attempts ({periodTotals.attempts})
              </span>
            </div>
          </div>
        ) : (
          <div style={emptyChartStyle}>
            <p style={emptyChartTextStyle}>No activity in this period</p>
          </div>
        )}
      </div>

      {/* Grade Breakdown Section */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Grade Breakdown</h3>
        <div style={gradeBreakdownContainerStyle}>
          {gradesWithSends.map((gradeData) => (
            <GradeBar
              key={gradeData.grade}
              grade={gradeData.grade}
              sendCount={gradeData.sendCount}
              maxCount={maxSendCount}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * StatCard displays a single statistic with a value and label
 */
function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div style={statCardStyle}>
      <span style={statValueStyle}>{value}</span>
      <span style={statLabelStyle}>{label}</span>
    </div>
  );
}

/**
 * PeriodButton for time period selection
 */
function PeriodButton({
  label,
  isSelected,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      style={{
        ...periodButtonStyle,
        ...(isSelected ? periodButtonSelectedStyle : {}),
      }}
      onClick={onClick}
      aria-pressed={isSelected}
    >
      {label}
    </button>
  );
}

/**
 * GradeBar displays a horizontal bar for a single grade
 */
function GradeBar({
  grade,
  sendCount,
  maxCount,
}: {
  grade: string;
  sendCount: number;
  maxCount: number;
}) {
  const widthPercent = (sendCount / maxCount) * 100;

  return (
    <div style={gradeBarRowStyle}>
      <span style={gradeLabelStyle}>{grade}</span>
      <div style={barContainerStyle}>
        <div
          style={{
            ...barFillStyle,
            width: `${widthPercent}%`,
          }}
        />
      </div>
      <span style={sendCountStyle}>{sendCount}</span>
    </div>
  );
}

/**
 * ActivityBar displays a vertical stacked bar for a single day/month
 */
function ActivityBar({
  date,
  sends,
  attempts,
  maxValue,
  isMonthly,
}: {
  date: string;
  sends: number;
  attempts: number;
  maxValue: number;
  isMonthly: boolean;
}) {
  const totalHeight = ((sends + attempts) / maxValue) * 100;
  const sendsHeight = sends > 0 ? (sends / (sends + attempts)) * 100 : 0;

  // Format date label
  const formatLabel = () => {
    if (isMonthly) {
      // YYYY-MM format - show M/YY
      const [year, month] = date.split('-');
      const monthNum = parseInt(month ?? '1', 10);
      const yearShort = year?.slice(2) ?? '';
      return `${monthNum}/${yearShort}`;
    }
    // YYYY-MM-DD format - show M/D
    const [, month, day] = date.split('-');
    const monthNum = parseInt(month ?? '1', 10);
    const dayNum = parseInt(day ?? '1', 10);
    return `${monthNum}/${dayNum}`;
  };

  return (
    <div style={activityBarContainerStyle}>
      <div style={activityBarWrapperStyle}>
        <div
          style={{
            ...activityBarStyle,
            height: `${totalHeight}%`,
          }}
        >
          {/* Sends portion (bottom) */}
          <div
            style={{
              ...activityBarSendsStyle,
              height: `${sendsHeight}%`,
            }}
          />
          {/* Attempts portion (top) */}
          <div
            style={{
              ...activityBarAttemptsStyle,
              height: `${100 - sendsHeight}%`,
            }}
          />
        </div>
      </div>
      <span style={activityBarLabelStyle}>{formatLabel()}</span>
    </div>
  );
}

// Styles following existing patterns from HistoryView.tsx

const containerStyle: CSSProperties = {
  padding: 'var(--spacing-md)',
};

const emptyTextStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
  padding: 'var(--spacing-lg)',
};

const quickStatsRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-around',
  gap: 'var(--spacing-md)',
  marginBottom: 'var(--spacing-lg)',
};

const statCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 'var(--spacing-md)',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-md)',
  flex: 1,
  minWidth: 0,
};

const statValueStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
};

const statLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  marginTop: 'var(--spacing-xs)',
};

const sectionStyle: CSSProperties = {
  marginBottom: 'var(--spacing-lg)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-sm)',
};

const periodButtonsStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--spacing-xs)',
  marginBottom: 'var(--spacing-md)',
};

const periodButtonStyle: CSSProperties = {
  padding: 'var(--spacing-xs) var(--spacing-sm)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  backgroundColor: 'transparent',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

const periodButtonSelectedStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-text-on-primary)',
  borderColor: 'var(--color-primary)',
};

const chartContainerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-md)',
};

const chartStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '2px',
  height: '120px',
  marginBottom: 'var(--spacing-sm)',
  overflowX: 'auto',
};

const activityBarContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: '1 1 0',
  minWidth: '20px',
  maxWidth: '40px',
};

const activityBarWrapperStyle: CSSProperties = {
  width: '100%',
  height: '100px',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};

const activityBarStyle: CSSProperties = {
  width: '80%',
  minHeight: '2px',
  borderRadius: 'var(--radius-sm)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const activityBarSendsStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary)',
  order: 2, // Bottom
};

const activityBarAttemptsStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary-light, #93c5fd)',
  order: 1, // Top
};

const activityBarLabelStyle: CSSProperties = {
  fontSize: '0.625rem',
  color: 'var(--color-text-secondary)',
  marginTop: '4px',
};

const legendStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: 'var(--spacing-md)',
  marginTop: 'var(--spacing-sm)',
};

const legendItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
};

const legendDotSendsStyle: CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-primary)',
};

const legendDotAttemptsStyle: CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-primary-light, #93c5fd)',
};

const emptyChartStyle: CSSProperties = {
  padding: 'var(--spacing-lg)',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-md)',
  textAlign: 'center',
};

const emptyChartTextStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
};

const gradeBreakdownContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
};

const gradeBarRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
};

const gradeLabelStyle: CSSProperties = {
  width: '32px',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  textAlign: 'right',
};

const barContainerStyle: CSSProperties = {
  flex: 1,
  height: '16px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-sm)',
  overflow: 'hidden',
};

const barFillStyle: CSSProperties = {
  height: '100%',
  backgroundColor: 'var(--color-primary)',
  borderRadius: 'var(--radius-sm)',
  transition: 'width 0.3s ease',
};

const sendCountStyle: CSSProperties = {
  width: '32px',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  textAlign: 'left',
};

export default StatisticsView;
